import { zodResolver } from '@hookform/resolvers/zod'
import { PersonOutline } from '@mui/icons-material'
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography
} from '@mui/material'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { AsyncSelect, Button, ControlledTextField } from 'src/components/forms'
import { HttpCacheUser } from 'src/http/cache/user/httpCacheUser'
import { HttpUser } from 'src/http/user/httpUser'
import { EntityWithIdFields } from 'src/types'
import { UserDTOFields, UserInsertDTO, UserViewDTO } from 'src/types/user/userData'
import { z } from 'zod'
import EditIcon from '@mui/icons-material/Edit'
import LockOpenIcon from '@mui/icons-material/LockOpen'

export type EditUserProps = {
    user: UserViewDTO
    onClose: () => void
    mode: 'edit' | 'view'
}

const schemaFormPostUsuario = z.object({
    [UserDTOFields.FirstName]: z.string(),
    [UserDTOFields.LastName]: z.string(),
    [UserDTOFields.Email]: z.string().email(),
    [UserDTOFields.PhoneNumber]: z.string().nullable().optional(),
    [UserDTOFields.UserPositionCode]: z.number()
})

type SchemaFormPostUsuario = z.infer<typeof schemaFormPostUsuario>

function EditUser({ user, onClose, mode }: EditUserProps) {
    const [formDisabled, setFormDisabled] = useState(mode === 'view')
    const { control, handleSubmit } = useForm<SchemaFormPostUsuario>({
        resolver: zodResolver(schemaFormPostUsuario),
        defaultValues: {
            ...user
        }
    })

    const submitUser = (data: UserInsertDTO) => {
        return user?.id ? HttpUser.updateUser(user.id, data) : HttpUser.insertUser(data)
    }

    const onSubmitWrapper = () => {
        let mensajeError = 'No se pudo guardar el usuario'

        const promise = new Promise<void>((resolve, reject) => {
            handleSubmit(
                formData => {
                    submitUser(formData)
                        .then(response => {
                            if (response.tieneError) {
                                mensajeError = response.descripcionError
                                reject(response.descripcionError)
                            } else {
                                setTimeout(onClose, 500)
                                resolve()
                            }
                        })
                        .catch(reject)
                },
                validationErrors => {
                    mensajeError = 'Revise el formulario'
                    reject(JSON.stringify(validationErrors))
                }
            )()
        })

        toast.promise(promise, {
            success: 'Guardado',
            error: () => mensajeError,
            loading: 'Guardando'
        })

        return promise
    }

    return (
        <>
            <DialogTitle sx={{ pb: 0 }}>
                <Stack direction='row' spacing={1.5} alignItems='center'>
                    <PersonOutline color='primary' />
                    <Typography variant='h6' fontWeight={600}>
                        {user[EntityWithIdFields.Id] === 0 ? 'Nuevo usuario' : 'Modificación de usuario'}
                    </Typography>
                    <Tooltip title={formDisabled ? 'Editar' : 'Solo lectura'}>
                        <IconButton size='small' onClick={() => setFormDisabled(!formDisabled)}>
                            {formDisabled ? (
                                <EditIcon color='primary' fontSize='small' />
                            ) : (
                                <LockOpenIcon color='primary' fontSize='small' />
                            )}
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: '24px !important', px: 5, pb: 4 }}>
                <Grid container spacing={5}>
                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name={UserDTOFields.FirstName}
                            label='Nombre'
                            fullWidth
                            InputProps={{ readOnly: formDisabled }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name={UserDTOFields.LastName}
                            label='Apellido'
                            fullWidth
                            InputProps={{ readOnly: formDisabled }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name={UserDTOFields.Email}
                            label='Email'
                            fullWidth
                            InputProps={{ readOnly: formDisabled }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AsyncSelect
                            control={control}
                            name={UserDTOFields.UserPositionCode}
                            label='Posición'
                            loadOptions={HttpCacheUser.getPositionList}
                            fullWidth
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name={UserDTOFields.PhoneNumber}
                            label='Teléfono'
                            fullWidth
                            InputProps={{ readOnly: formDisabled }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant='text' color='inherit' onClick={onClose}>
                    Cancelar
                </Button>
                {!formDisabled && (
                    <Button variant='contained' color='primary' promise={onSubmitWrapper}>
                        Guardar
                    </Button>
                )}
            </DialogActions>
        </>
    )
}

export default EditUser
